/**
 * ExceptionsTab — Slice A "foolproof" exception queue.
 *
 * Surfaces everything the reconciliation engine flagged that needs a human:
 *   - MissedTouch      → care tasks past their grace window
 *   - MissingSchedule  → an active enrolment with no care schedule
 *   - Unassigned       → a loan with no broker/officer assigned
 *   - SendFailure      → (Slice D) permanent email/SMS delivery failure
 *   - SLABreach        → (Slice B) detractor not followed up within 48h
 *
 * Each exception can be "resolved" with an optional note — the resolution
 * itself is written to the immutable audit log so we can prove the failure
 * was acted on.
 *
 * Spec: §15 — Reliability guarantees → exception queue + recon engine.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Activity, AlertTriangle, CheckCircle2, RefreshCw, ShieldAlert, Clock, UserX, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const TYPE_META = {
  MissedTouch:      { icon: Clock,        label: 'Missed touch',     accent: 'red'   },
  MissingSchedule:  { icon: ShieldAlert,  label: 'No schedule',      accent: 'amber' },
  Unassigned:       { icon: UserX,        label: 'Unassigned',       accent: 'amber' },
  SendFailure:      { icon: Mail,         label: 'Send failure',     accent: 'red'   },
  SLABreach:        { icon: AlertTriangle,label: 'SLA breach',       accent: 'red'   },
  SchedulerStall:   { icon: Activity,     label: 'Scheduler stall',  accent: 'red'   },
  ReconMismatch:    { icon: AlertTriangle,label: 'Recon mismatch',   accent: 'amber' },
};

const ACCENT_STYLES = {
  red:   'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
};

const ExceptionsTab = ({ onOpenLoan }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [exceptions, setExceptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [heartbeat, setHeartbeat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open'); // open | resolved | all

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s, h] = await Promise.all([
        fetch(`${backendUrl}/api/settled-loans/exceptions?status=${statusFilter}`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/exceptions/stats`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/heartbeat`, { headers: authHeaders }),
      ]);
      if (e.ok) setExceptions(await e.json());
      if (s.ok) setStats(await s.json());
      if (h.ok) setHeartbeat(await h.json());
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const runRecon = async () => {
    setSweeping(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/reconciliation/run`, { method: 'POST', headers: authHeaders });
      if (!r.ok) throw new Error('Recon failed');
      const j = await r.json();
      toast.success(`Reconciliation ran · checked ${j.checked} · ${j.missed_touch + j.missing_schedule + j.unassigned} issues`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setSweeping(false); }
  };

  const resolve = async (excId) => {
    const note = window.prompt('Resolution note (optional):') || '';
    setBusyId(excId);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/exceptions/${excId}/resolve${note ? `?note=${encodeURIComponent(note)}` : ''}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Resolve failed');
      toast.success('Exception resolved');
      setExceptions((prev) => prev.filter((x) => x.id !== excId));
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="exceptions-tab">
      {/* Heartbeat strip */}
      <HeartbeatStrip heartbeat={heartbeat} />

      {/* Controls */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[
            { k: 'open', label: 'Open' },
            { k: 'resolved', label: 'Resolved' },
            { k: 'all', label: 'All' },
          ].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setStatusFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                statusFilter === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid={`exceptions-status-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={runRecon}
          disabled={sweeping}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50"
          data-testid="run-recon-btn"
        >
          {sweeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Run reconciliation
        </button>
      </div>

      {/* Status board — green/amber/red bucket counts */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatusCard label="Open exceptions"  value={stats.open_total} accent={stats.open_total ? 'red' : 'emerald'} />
          <StatusCard label="Missed touches"   value={stats.by_type?.MissedTouch ?? 0} accent={(stats.by_type?.MissedTouch ?? 0) ? 'red' : 'slate'} />
          <StatusCard label="Missing schedule" value={stats.by_type?.MissingSchedule ?? 0} accent={(stats.by_type?.MissingSchedule ?? 0) ? 'amber' : 'slate'} />
          <StatusCard label="Unassigned"       value={stats.by_type?.Unassigned ?? 0} accent={(stats.by_type?.Unassigned ?? 0) ? 'amber' : 'slate'} />
        </div>
      )}

      {/* Last recon timestamp */}
      {stats?.last_recon_pass && (
        <p className="text-[11px] text-slate-500 italic px-1">
          Last successful reconciliation: {fmtDateTime(stats.last_recon_pass)}
        </p>
      )}

      {/* Exception list */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        {exceptions.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="exceptions-empty">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">All clear</p>
            <p className="text-xs">
              {statusFilter === 'open' ? 'No open exceptions — the system is foolproof for now.' : 'Nothing to show in this view.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {exceptions.map((x) => {
              const meta = TYPE_META[x.type] || { icon: AlertTriangle, label: x.type, accent: 'amber' };
              const I = meta.icon;
              return (
                <li key={x.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid="exception-row">
                  <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${ACCENT_STYLES[meta.accent]}`}>
                    <I className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide ${ACCENT_STYLES[meta.accent]}`}>{meta.label}</span>
                      {x.loan_number && (
                        <button
                          type="button"
                          onClick={() => onOpenLoan?.(x.entity_id)}
                          className="font-mono text-[11px] text-blue-700 dark:text-blue-300 hover:underline"
                        >
                          {x.loan_number}
                        </button>
                      )}
                      {x.client_name && (
                        <span className="text-xs font-semibold text-slate-900 dark:text-white truncate">{x.client_name}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{x.detail}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Raised {fmtDateTime(x.raised_at)}</p>
                  </div>
                  {x.status === 'open' && (
                    <button
                      type="button"
                      disabled={busyId === x.id}
                      onClick={() => resolve(x.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50 shrink-0"
                      data-testid="exception-resolve-btn"
                    >
                      {busyId === x.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Resolve
                    </button>
                  )}
                  {x.status === 'resolved' && (
                    <span className="text-[11px] text-slate-400 italic shrink-0">Resolved {fmtDateTime(x.resolved_at)}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const HeartbeatStrip = ({ heartbeat }) => {
  if (!heartbeat) return null;
  const alive = !!heartbeat.alive;
  return (
    <div
      className={`px-3 py-2 rounded-xl border flex items-center gap-2 text-xs ${
        alive
          ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300'
          : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300'
      }`}
      data-testid="scheduler-heartbeat-strip"
    >
      <Activity className={`w-3.5 h-3.5 ${alive ? 'animate-pulse' : ''}`} />
      <span className="font-semibold">Scheduler:</span>
      {alive
        ? <span>Alive · last beat {heartbeat.age_seconds}s ago</span>
        : <span>STALLED — no beat for {heartbeat.age_seconds ?? '∞'}s. Automation may be paused.</span>}
    </div>
  );
};

const StatusCard = ({ label, value, accent }) => {
  const accents = {
    red:     'text-red-700 dark:text-red-300',
    amber:   'text-amber-700 dark:text-amber-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    slate:   'text-slate-700 dark:text-slate-200',
  };
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-800 grid place-items-center ${accents[accent]}`}>
        <AlertTriangle className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <div className={`text-lg font-bold leading-tight ${accents[accent]}`}>{value}</div>
      </div>
    </div>
  );
};

export default ExceptionsTab;
